import React from 'react';

import {
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { GROUP_LABELS, marginEvidence, money, percent, propertyGroup } from './review';
import type { GroupBy, HealthProperty, ReviewWindow } from './types';

export default function PortfolioGroups({
  properties,
  groupBy,
  window,
  onSelect,
}: {
  properties: HealthProperty[];
  groupBy: GroupBy;
  window: ReviewWindow;
  onSelect: (group: string) => void;
}) {
  if (groupBy === 'all' || !properties.length) return null;
  const groups = [...new Set(properties.map((property) => propertyGroup(property, groupBy)))].sort();
  const total = marginEvidence(properties, window).observedSubtotal;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Compare {groupBy === 'country' ? 'countries' : 'commercial contacts'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Observed subtotals include only hotels with financial evidence. Coverage shows which hotels contribute;
          missing evidence is not zero.
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="Portfolio group comparison">
            <TableHead>
              <TableRow>
                <TableCell>{GROUP_LABELS[groupBy]}</TableCell>
                <TableCell align="right">Hotels</TableCell>
                <TableCell align="right">
                  Observed LPC margin
                  <br />
                  AUD, after refunds
                </TableCell>
                <TableCell align="right">Share of observed margin</TableCell>
                <TableCell>Margin coverage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => {
                const rows = properties.filter((property) => propertyGroup(property, groupBy) === group);
                const evidence = marginEvidence(rows, window);
                const margin = evidence.observedSubtotal;
                const covered = evidence.contributingHotels;
                return (
                  <TableRow key={group}>
                    <TableCell>
                      <Button onClick={() => onSelect(group)} sx={{ textAlign: 'left', p: 0 }}>
                        {group}
                      </Button>
                    </TableCell>
                    <TableCell align="right">{rows.length}</TableCell>
                    <TableCell align="right">{money(margin)}</TableCell>
                    <TableCell align="right">
                      {percent(total != null && total !== 0 && margin != null ? margin / total : null)}
                    </TableCell>
                    <TableCell>
                      {covered} / {rows.length} hotels
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
